import { useMemo, useState } from 'react';
import { useFormData } from '../../hooks/useFormData';
import { getOverviewStatus } from '../../utils/config-utils';
import { useFormConfig } from '../../hooks/useFormConfig';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import { useUiContext } from '../../context/UiContext';
import { DeclarationWrapper } from '../OverviewScreen/Wrappers';
import { Status } from '../../types';
import { Link } from 'react-router-dom';
import content from '../../content';
import { SummaryAnswers } from '../../components/SummaryAnswers/SummaryAnswers';
import config from '../../config';

export const FormSummary = () => {
  const { isLoading } = useUiContext();
  const { formConfig, enabledSubsections } = useFormConfig();
  const { sections } = formConfig;

  const allSections = useMemo(() => {
    return sections.flatMap((section) => section.subsections.map((subsection) => subsection.slug));
  }, [sections]);

  const [openSections, setOpenSections] = useState<string[]>([allSections?.[0]]);

  // fetch personal data
  const { data } = useFormData();

  const progressData = useMemo(
    () => getOverviewStatus(sections, enabledSubsections, data),
    [sections, enabledSubsections, data],
  );

  const areAllSectionsOpen = useMemo(() => {
    return sections.every((section) =>
      section.subsections.every((subsection) => openSections.includes(subsection.slug)),
    );
  }, [openSections, sections]);

  const handleToggleAllSections = () => {
    setOpenSections(areAllSectionsOpen ? [] : allSections);
  };

  const handleToggleSection = (subsectionSlug: string) => {
    setOpenSections((prev) =>
      prev.includes(subsectionSlug)
        ? prev.filter((slug) => slug !== subsectionSlug)
        : [...prev, subsectionSlug],
    );
  };

  return (
    <>
      {isLoading ? (
        <PageTemplate title="">
          <h2 className="govuk-heading-m">Loading...</h2>
        </PageTemplate>
      ) : (
        <PageTemplate title="Check your answers" fullWidth>
          <div data-testid="overview">
            <span
              className={`govuk-link  govuk-body summary-list__toggle ${
                areAllSectionsOpen ? 'summary-list__toggle--open' : 'summary-list__toggle--closed'
              }`}
              onClick={handleToggleAllSections}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleToggleAllSections();
                }
              }}
            >
              {areAllSectionsOpen ? 'Hide all sections' : 'Show all sections'}
            </span>
            <ol className="govuk-list" data-testid="overview-sectionList">
              {sections.map((section) =>
                section.subsections.map((subsection) => {
                  return (
                    <li key={subsection.slug} className="summary-list__item">
                      <div className="summary-list__item-header">
                        <h3
                          data-testid={`overview-subsectionList-${subsection.slug}Link`}
                          className="govuk-heading-l"
                          onClick={() => handleToggleSection(subsection.slug)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleToggleSection(subsection.slug);
                            }
                          }}
                        >
                          {subsection.title}
                        </h3>
                        <span
                          className={`govuk-link govuk-body summary-list__toggle ${
                            openSections.includes(subsection.slug)
                              ? 'summary-list__toggle--open'
                              : 'summary-list__toggle--closed'
                          }`}
                          onClick={() => handleToggleSection(subsection.slug)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleToggleSection(subsection.slug);
                            }
                          }}
                        >
                          <span className="summary-list__item-header-button-text">
                            {openSections.includes(subsection.slug) ? 'Hide' : 'Show'}
                          </span>
                        </span>
                      </div>
                      <div
                        className={`govuk-body ${
                          openSections.includes(subsection.slug) ? '' : 'govuk-!-display-none'
                        }`}
                      >
                        <SummaryAnswers
                          pages={subsection.pages}
                          subsection={subsection}
                          urlPath={`/${section.slug}/${subsection.slug}`}
                        />
                      </div>
                    </li>
                  );
                }),
              )}
              {!!sections.length && (
                <DeclarationWrapper showSections={false} sections={sections}>
                  <h3 className="govuk-heading-l">Declaration</h3>
                  <li className="summary-list__item">
                    {Object.values(progressData).some(
                      (status: string) => status !== Status.Completed,
                    ) ? (
                      // eslint-disable-next-line jsx-a11y/anchor-is-valid
                      <div className="govuk-link">{content.submitQuestionnaireLink}</div>
                    ) : (
                      <Link className="govuk-link" to={config.urls.declaration}>
                        {content.submitQuestionnaireLink}
                      </Link>
                    )}
                  </li>
                </DeclarationWrapper>
              )}
            </ol>
          </div>
        </PageTemplate>
      )}
    </>
  );
};
